import { ref, toRaw } from 'vue'

import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia'

import { util } from '@/Helpers'
import { useStorage } from '@vueuse/core'

import * as Sentry from "@sentry/vue"

import moment from 'moment'
import { useAuthStore, useClientSingleStore } from '@/Stores'

/*
    This store is the store dedicated for the client list, generated primarily from the jobs and
    nearby client APIs.
*/

export const useClientStore = defineStore('client', () => {

    const authStore = useAuthStore()
    const { user, userWithId } = storeToRefs(authStore)

    const clientSingleStore = useClientSingleStore()

    const clients = ref({})
    const lastUpdated = ref(null)

    const loading = ref(false)

    const dataCompletionLoading = ref(false)
    const dataCompletionRefreshTimer = ref(null)
    const dataCompletionRefreshTime = 120000

    const dataCompletionProgress = ref([])
    const dataCompletionUserContext = useStorage('staffContext', structuredClone(toRaw(userWithId.value)))

    //
    // Hydration
    //

    async function hydrateFromJobs(jobsData) {
        clients.value = Object.assign(clients.value, util.convertArrayToObject(jobsData.map(job => job.client), 'id'))

        // Reshape phone numbers
        Object.values(clients.value).forEach(client => {
            if (Array.isArray(client.phone_numbers)) {
                client.phone_numbers = util.convertArrayToObject(client.phone_numbers, 'type')
            }
        })

        lastUpdated.value = moment.utc()

    }


    // Load client data to store
    async function hydrateFromNearby(clientsData) {
        clients.value = Object.assign(clients.value, util.convertArrayToObject(clientsData, 'id'))

        // Reshape phone numbers
        Object.values(clients.value).forEach(client => {
            if (Array.isArray(client.phone_numbers)) {
                client.phone_numbers = util.convertArrayToObject(client.phone_numbers, 'type')
            }
        })

        lastUpdated.value = moment.utc()

    }


    async function retrieveNearbyClients(address, coffee_user_profile_id = null) {
        const params = {
            address: address
        }
        if (coffee_user_profile_id) {
            params.coffee_user_profile_id = coffee_user_profile_id
        }

        return await new Promise((resolve, reject) => {
            axios.get(
                '/api/v1/clients/nearby',
                {
                    params: params
                },
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                // authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function getClientById(client_id) {
        return await new Promise((resolve, reject) => {
            axios.get(
                `/api/v1/clients/${client_id}`
            ).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function getClientForCompletionData($staff_id) {
        const params = {
            staff_id: $staff_id
        }
        return await new Promise((resolve, reject) => {
            axios.get('/api/v2/clients/completion', {
                params: params,
            }).then(response => {
                resolve(response)
            }).catch((err) => {
                authStore.handleError(err)
                reject({ "error": err })
            })
        });
    }

    async function hydrateClientCompletionData(clientsData) {
        clientCompletionList.value = clientsData

        // Reshape phone numbers
        clientCompletionList.value.forEach(client => {
            if (Array.isArray(client.phone_numbers)) {
                client.phone_numbers = util.convertArrayToObject(client.phone_numbers, 'type')
            }
        })

        lastUpdated.value = moment.utc()
    }

    const clientCompletionList = ref([])

    async function refreshDataCompletionList() {
        const staffId = dataCompletionUserContext.value?.user_id ?? dataCompletionUserContext.value?.id

        if (!staffId) {
            return
        }

        try {
            dataCompletionLoading.value = true
            const response = await getClientForCompletionData(staffId)
            await hydrateClientCompletionData(response.data)
        } catch (error) {
            authStore.handleError(error?.error ?? error)
        } finally {
            resetDataCompletionRefreshTimer()
            dataCompletionLoading.value = false
        }
    }

    function resetDataCompletionRefreshTimer() {
        clearTimeout(dataCompletionRefreshTimer.value)
        dataCompletionRefreshTimer.value = setTimeout(refreshDataCompletionList, dataCompletionRefreshTime)
    }

    function stopDataCompletionRefreshTimer() {
        clearTimeout(dataCompletionRefreshTimer.value)
        dataCompletionRefreshTimer.value = null
    }

    function hydrateSingleClient(clientData) {
        const existingClientIndex = clientCompletionList.value.findIndex(client => client.id === clientData.id)
        if (existingClientIndex !== -1) {
            clientCompletionList.value[existingClientIndex] = clientData
        } else {
            clientCompletionList.value.push(clientData)
        }

        // Also update the clients object if it exists there
        clients.value[clientData.id] = clientData

        // Reshape phone numbers for the new/updated client
        if (Array.isArray(clientData.phone_numbers)) {
            clientData.phone_numbers = util.convertArrayToObject(clientData.phone_numbers, 'type')
        }

        lastUpdated.value = moment.utc()
    }


    function getOverallDataCompletionProgress(tableData) {
        if (tableData.value.length === 0) {
            dataCompletionProgress.value = [];
            return [];
        }

        const totalClients = tableData.value.length;
        let poor = 0, good = 0, great = 0;

        tableData.value.forEach(client => {
            const dataCompletionStatus = clientSingleStore.getProfileDataCompletionStatus(client.data_completion_status);

            if (dataCompletionStatus === 'great') {
                great++;
            } else if (dataCompletionStatus === 'good') {
                good++;
            } else {
                poor++;
            }
        })

        const poorPercent = Math.round((poor / totalClients) * 100);
        const goodPercent = Math.round((good / totalClients) * 100);
        const greatPercent = Math.round((great / totalClients) * 100);

        const result = [
            { label: 'Good', color: '#679950', value: goodPercent },
            { label: 'Great', color: '#e1c244', value: greatPercent },
            { label: 'Poor / Incomplete', color: '#a95440', value: poorPercent },
        ]

        dataCompletionProgress.value = result;
        return result;
    }

    function getDataCompletionLeaderBoard(tableData) {

        let segments = []

        const totalResults = tableData.value.filter(client => client.aggregate_updated_by !== null).length

        if (totalResults === 0) {
            return segments;
        }

        const myEditsCount = tableData.value.filter(client => client.aggregate_updated_by?.id === user.value.user_id).length

        segments.push({ label: 'You: ' + myEditsCount, color: '#008AAB', value: Math.round((myEditsCount / totalResults) * 100) })

        if (dataCompletionUserContext.value.id !== user.value.user_id) {

            const staffContextEditsCount = tableData.value.filter(client => client.aggregate_updated_by?.id === dataCompletionUserContext.value.id).length

            segments.push({ label: dataCompletionUserContext.value.first_name + ': ' + staffContextEditsCount, color: '#005D73', value: Math.round((staffContextEditsCount / totalResults) * 100) })

        }

        const otherEditsCount = tableData.value.filter(client => client.aggregate_updated_by?.id !== user.value.user_id && client.aggregate_updated_by?.id !== dataCompletionUserContext.value.id && client.aggregate_updated_by !== null).length

        segments.push({ label: 'Others: ' + otherEditsCount, color: '#d4d4d8', value: Math.round((otherEditsCount / totalResults) * 100) })

        return segments;

    }


    return {
        hydrateFromJobs, hydrateFromNearby, retrieveNearbyClients,
        getClientById, getClientForCompletionData, hydrateClientCompletionData, hydrateSingleClient,
        clients, clientCompletionList, lastUpdated, loading,
        getOverallDataCompletionProgress, getDataCompletionLeaderBoard,
        dataCompletionLoading, dataCompletionProgress, dataCompletionUserContext,
        refreshDataCompletionList, resetDataCompletionRefreshTimer, stopDataCompletionRefreshTimer
    }

})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useClientStore, import.meta.hot))
}
